import {
	createReactBlockSpec,
	createReactInlineContentSpec,
	useBlockNoteEditor,
	useEditorState,
	useExtensionState
} from "@blocknote/react"
import type { DefaultBlockSchema } from "@blocknote/core"
import { SideMenuExtension } from "@blocknote/core/extensions"
import {
	DragHandleMenu,
	SideMenu,
	SideMenuController,
	type SideMenuProps
} from "@blocknote/react"
import { createContext, useContext, useState } from "react"
import { toast } from "sonner"
import {
	AlignLeft,
	CalendarClock,
	Copy,
	GitBranch,
	ListChecks,
	SlidersHorizontal,
	TextCursorInput,
	Trash2
} from "lucide-react"
import type { useFormPagination } from "~/hooks/useFormPagination"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import { Textarea } from "~/components/ui/textarea"
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from "~/components/ui/dialog"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "~/components/ui/select"
import { Switch } from "~/components/ui/switch"
import {
	DropdownMenuItem,
	DropdownMenuSeparator
} from "~/components/ui/dropdown-menu"
import {
	formAnswerInlineContentConfig,
	formPageBreakBlockConfig,
	questionBlockConfig
} from "~/lib/editor-content"
import {
	visibleQuestionFields,
	parseFormLogic,
	renderPiping,
	type FormFieldLike
} from "~/lib/forms"
import {
	type FormFieldLogic,
	type FormFieldType,
	type LogicCondition
} from "~/lib/enums"
import { QuestionInput, type FormUploadFn } from "./FormFieldView"

export type FormFillState = {
	/** Answer controls are interactive only in fill mode. */
	filling: boolean
	pagination?: ReturnType<typeof useFormPagination>
	upload: FormUploadFn
	fields: FormFieldLike[]
	errors: Record<string, string>
	answers: { fieldId: string; value: unknown }[]
	setAnswer: (fieldId: string, value: unknown) => void
}

export const FormFillContext = createContext<FormFillState | null>(null)

const useQuestionFillState = () => useContext(FormFillContext)

export const FormAnswerInline = createReactInlineContentSpec(
	formAnswerInlineContentConfig,
	{
		render: ({ inlineContent }) => {
			const fill = useQuestionFillState()
			return (
				<span>
					{fill?.filling
						? renderPiping(
								`{{${inlineContent.props.fieldId}}}`,
								fill.fields,
								fill.answers
							)
						: `@${inlineContent.props.label || "Answer"}`}
				</span>
			)
		},
		toExternalHTML: ({ inlineContent }) => (
			<span>{`{{${inlineContent.props.fieldId}}}`}</span>
		)
	}
)

export const FormPageBreak = createReactBlockSpec(formPageBreakBlockConfig, {
	render: ({ contentRef }) => (
		<div className="flex w-full items-center border-t border-dashed border-stroke py-4">
			<div
				ref={contentRef}
				role="heading"
				aria-level={2}
				className="min-w-0 flex-1 text-xl font-semibold leading-snug text-text-main"
			/>
		</div>
	)
})

export const questionSlashItems: { value: FormFieldType; label: string }[] = [
	{ value: "shortText", label: "Short text" },
	{ value: "longText", label: "Long text" },
	{ value: "number", label: "Number" },
	{ value: "email", label: "Email" },
	{ value: "phone", label: "Phone" },
	{ value: "url", label: "URL" },
	{ value: "signature", label: "Signature" },
	{ value: "dropdown", label: "Multiple choice dropdown" },
	{ value: "checkbox", label: "Checkbox" },
	{ value: "checkboxGroup", label: "Checkbox group" },
	{ value: "radioGroup", label: "Radio group" },
	{ value: "date", label: "Date, time or date+time" },
	{ value: "fileUpload", label: "File upload" }
]

const questionTypeLabel = (qtype: string) =>
	questionSlashItems.find((item) => item.value === qtype)?.label ?? qtype

const operatorLabels: { value: LogicCondition["operator"]; label: string }[] = [
	{ value: "equals", label: "Is" },
	{ value: "notEquals", label: "Is not" },
	{ value: "contains", label: "Contains" },
	{ value: "notContains", label: "Does not contain" },
	{ value: "isEmpty", label: "Is empty" },
	{ value: "isNotEmpty", label: "Is filled in" }
]

const blockLabelText = (block: { content?: unknown }): string =>
	Array.isArray(block.content)
		? block.content
				.map((part) => {
					if (part === null || typeof part !== "object") return ""
					const typed = part as {
						type?: string
						text?: string
						content?: { text?: string }[]
						props?: { label?: string }
					}
					if (typed.type === "formAnswer")
						return `@${typed.props?.label || "Answer"}`
					if (typed.type === "text") return typed.text ?? ""
					if (typed.type === "link")
						return (typed.content ?? []).map((t) => t.text ?? "").join("")
					return ""
				})
				.join("")
		: ""

const questionOptionsFromProps = (props: Record<string, unknown>) => ({
	allowOther: props.allowOther === true,
	choices: typeof props.choices === "string" ? props.choices.split("\n") : [],
	includesTime: props.includesTime === true,
	includesDate: props.includesDate !== false,
	min:
		props.min !== "" && props.min !== undefined ? Number(props.min) : undefined,
	max:
		props.max !== "" && props.max !== undefined ? Number(props.max) : undefined
})

/** Rich question text and answer placeholder are independently editable. */
export const QuestionBlock = createReactBlockSpec(questionBlockConfig, {
	render: function QuestionBlockRender({ block, contentRef, editor }) {
		const fill = useQuestionFillState()
		const props = block.props as Record<string, string | number | boolean>
		const qtype = String(props.qtype ?? "shortText") as FormFieldType
		const fillState = fill?.filling ? fill : null
		const answerValue =
			fillState?.answers.find((answer) => answer.fieldId === block.id)?.value ??
			null

		const field = fillState?.fields.find((field) => field.id === block.id)
		const isVisible =
			!fillState ||
			!field ||
			visibleQuestionFields(fillState.fields, fillState.answers).some(
				(visible) => visible.id === field.id
			)
		const error = fillState?.errors[block.id]
		if (!isVisible)
			return (
				<div
					contentEditable={false}
					data-question-hidden="true"
					className="hidden"
				>
					<div ref={contentRef} />
				</div>
			)

		return (
			<div className="flex w-full min-w-0 flex-col gap-1.5 py-1.5">
				<div className="flex items-baseline gap-1">
					<div
						ref={contentRef}
						className="relative min-w-0 font-medium"
						data-question-label
						data-empty={!blockLabelText(block)}
					/>
					{props.required === true && (
						<span aria-hidden className="text-destructive">
							*
						</span>
					)}
				</div>
				{String(props.description ?? "") && (
					<p className="text-xs text-text-sub">
						{fillState
							? renderPiping(
									String(props.description ?? ""),
									fillState.fields,
									fillState.answers
								)
							: String(props.description ?? "")}
					</p>
				)}
				<div
					contentEditable={false}
					className={
						qtype === "longText" ||
						qtype === "signature" ||
						qtype === "fileUpload" ||
						qtype === "radioGroup" ||
						qtype === "checkboxGroup"
							? "w-full"
							: "w-full max-w-80"
					}
				>
					<QuestionInput
						qtype={qtype}
						label={blockLabelText(block) || questionTypeLabel(qtype)}
						invalid={Boolean(error)}
						options={questionOptionsFromProps(props)}
						placeholder={String(props.placeholder ?? "")}
						editing={!fillState}
						value={fillState ? answerValue : String(props.placeholder ?? "")}
						onChange={(value) => {
							if (fillState) fillState.setAnswer(block.id, value)
							else
								editor.updateBlock(block, {
									props: { placeholder: String(value ?? "") }
								})
						}}
						onChoicesChange={(choices) =>
							editor.updateBlock(block, {
								props: { choices: choices.join("\n") }
							})
						}
						disabled={!fillState && !editor.isEditable}
						upload={fillState?.upload ?? (async () => ({ url: "about:blank" }))}
					/>
				</div>
				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}
			</div>
		)
	}
})

const QuestionSettingsContext = createContext<
	| ((
			id: string,
			section: "options" | "description" | "logic" | "defaultAnswer"
	  ) => void)
	| null
>(null)

function QuestionDragHandleMenu() {
	const editor = useBlockNoteEditor<
		DefaultBlockSchema & {
			question: typeof questionBlockConfig
			formPageBreak: typeof formPageBreakBlockConfig
		}
	>()
	const openSettings = useContext(QuestionSettingsContext)
	const hoveredId = useExtensionState(SideMenuExtension, {
		selector: (state) => state?.block?.id
	})
	// The side menu extension only emits on hover changes; follow editor
	// document changes separately so toggles/colors render their fresh value.
	const block = useEditorState({
		selector: () => (hoveredId ? editor.getBlock(hoveredId) : undefined)
	})
	if (!block || (block.type !== "question" && block.type !== "formPageBreak"))
		return <DragHandleMenu />
	const type = block.type === "question" ? block.props.qtype : "formPageBreak"
	const typeLabel =
		block.type === "question" ? questionTypeLabel(type) : "Page break"
	const label = blockLabelText(block) || typeLabel

	const duplicate = () => {
		const copy = structuredClone(block)
		copy.id = crypto.randomUUID()
		const remaining = [...copy.children]
		while (remaining.length) {
			const next = remaining.pop()!
			next.id = crypto.randomUUID()
			remaining.push(...next.children)
		}
		editor.insertBlocks([copy], block, "after")
	}

	// Mirrors BlockNote's RemoveBlockItem: deletes the hovered block, or the
	// whole selection when the hovered block is part of it.
	const remove = () => {
		const selected = editor.getSelection()?.blocks
		const selectedBlock = selected?.some((item) => item.id === block.id)
		editor.removeBlocks(selectedBlock && selected ? selected : [block])
	}

	return (
		<DragHandleMenu>
			<div className="flex items-center gap-2 px-2.5 py-2">
				<div className="min-w-0 truncate text-sm font-medium text-text-main">
					{label}
				</div>
				<div className="ml-auto shrink-0 text-xs text-text-small">
					{typeLabel}
				</div>
			</div>
			<DropdownMenuSeparator />
			{block.type === "question" && (
				<DropdownMenuItem
					className="px-2.5 py-2"
					onSelect={(event) => {
						event.preventDefault()
						editor.updateBlock(block, {
							props: { required: !block.props.required }
						})
					}}
				>
					Required
					<Switch
						checked={block.props.required === true}
						className="ml-auto pointer-events-none"
						tabIndex={-1}
					/>
				</DropdownMenuItem>
			)}
			{block.type === "question" &&
				type !== "signature" &&
				type !== "fileUpload" && (
					<DropdownMenuItem
						className="px-2.5 py-2"
						onClick={() => openSettings?.(block.id, "defaultAnswer")}
					>
						<TextCursorInput />
						Default answer
					</DropdownMenuItem>
				)}
			{block.type === "question" &&
				[
					"shortText",
					"longText",
					"number",
					"dropdown",
					"radioGroup",
					"checkboxGroup",
					"date"
				].includes(type) && (
					<DropdownMenuItem
						className="px-2.5 py-2"
						onClick={() => openSettings?.(block.id, "options")}
					>
						{type === "date" ? (
							<CalendarClock />
						) : type === "dropdown" ||
						  type === "radioGroup" ||
						  type === "checkboxGroup" ? (
							<ListChecks />
						) : (
							<SlidersHorizontal />
						)}
						{type === "date"
							? "Date and time"
							: type === "dropdown" ||
								  type === "radioGroup" ||
								  type === "checkboxGroup"
								? "Options"
								: "Limits"}
					</DropdownMenuItem>
				)}
			{block.type === "question" && (
				<DropdownMenuItem
					className="px-2.5 py-2"
					onClick={() => openSettings?.(block.id, "description")}
				>
					<AlignLeft />
					Description
				</DropdownMenuItem>
			)}
			<DropdownMenuItem
				className="px-2.5 py-2"
				onClick={() => openSettings?.(block.id, "logic")}
			>
				<GitBranch />
				Conditional logic
			</DropdownMenuItem>
			<DropdownMenuSeparator />
			<DropdownMenuItem className="px-2.5 py-2" onClick={duplicate}>
				<Copy />
				Duplicate
			</DropdownMenuItem>
			<DropdownMenuItem
				className="px-2.5 py-2"
				variant="destructive"
				onClick={remove}
			>
				<Trash2 />
				Delete
			</DropdownMenuItem>
		</DragHandleMenu>
	)
}

function QuestionSideMenu(props: SideMenuProps) {
	return <SideMenu {...props} dragHandleMenu={QuestionDragHandleMenu} />
}

/** Kept outside the transient drag menu so dialogs retain their target after it closes. */
export function QuestionSideMenuController() {
	const editor = useBlockNoteEditor<
		DefaultBlockSchema & {
			question: typeof questionBlockConfig
			formPageBreak: typeof formPageBreakBlockConfig
		}
	>()
	const [selection, setSelection] = useState<{
		id: string
		section: "options" | "description" | "logic" | "defaultAnswer"
	} | null>(null)
	const block = selection ? editor.getBlock(selection.id) : undefined
	const earlier = []
	if (selection?.section === "logic") {
		const remaining = [...editor.document]
		while (remaining.length) {
			const candidate = remaining.shift()!
			if (candidate.id === selection.id) break
			if (candidate.type === "question")
				earlier.push({
					id: candidate.id,
					label:
						blockLabelText(candidate) ||
						questionTypeLabel(candidate.props.qtype)
				})
			remaining.unshift(...candidate.children)
		}
	}
	return (
		<QuestionSettingsContext.Provider
			value={(id, section) => setSelection({ id, section })}
		>
			<SideMenuController sideMenu={QuestionSideMenu} />
			<Dialog
				open={selection !== null}
				onOpenChange={(open) => {
					if (!open) setSelection(null)
				}}
			>
				{selection &&
					(block?.type === "question" || block?.type === "formPageBreak") && (
						<DialogContent>
							<DialogHeader>
								<DialogTitle>
									{selection.section === "defaultAnswer"
										? "Default answer"
										: selection.section === "logic"
											? "Conditional logic"
											: selection.section === "description"
												? "Description"
												: `${block.type === "question" ? questionTypeLabel(block.props.qtype) : "Page"} settings`}
								</DialogTitle>
							</DialogHeader>
							{selection.section === "logic" ? (
								<LogicDialogBody
									key={block.id}
									initial={parseFormLogic(block.props.logic)}
									earlier={earlier}
									onSave={(logic) => {
										if (editor.getBlock(block.id)?.type === block.type)
											editor.updateBlock(block, {
												props: { logic: logic ? JSON.stringify(logic) : "" }
											})
										setSelection(null)
									}}
									onCancel={() => setSelection(null)}
								/>
							) : (
								<QuestionSettings
									key={`${block.id}:${selection.section}`}
									blockId={block.id}
									section={selection.section}
									onClose={() => setSelection(null)}
								/>
							)}
						</DialogContent>
					)}
			</Dialog>
		</QuestionSettingsContext.Provider>
	)
}

function QuestionSettings({
	blockId,
	section,
	onClose
}: {
	blockId: string
	section: "options" | "description" | "defaultAnswer"
	onClose: () => void
}) {
	const editor = useBlockNoteEditor<
		DefaultBlockSchema & { question: typeof questionBlockConfig }
	>()
	const block = editor.getBlock(blockId)
	const [draft, setDraft] = useState(() =>
		block?.type === "question" ? { ...block.props } : null
	)
	if (!draft || block?.type !== "question") return null
	const choices =
		draft.qtype === "dropdown" ||
		draft.qtype === "radioGroup" ||
		draft.qtype === "checkboxGroup"
	return (
		<form
			className="flex flex-col gap-4"
			onSubmit={(event) => {
				event.preventDefault()
				const current = editor.getBlock(blockId)
				if (current?.type !== "question") return onClose()
				if (section === "options") {
					if (
						choices &&
						!draft.choices.split("\n").some((choice) => choice.trim())
					)
						return toast.error("Add at least one option.")
					if (
						(draft.min !== "" && !Number.isFinite(Number(draft.min))) ||
						(draft.max !== "" && !Number.isFinite(Number(draft.max))) ||
						(draft.min !== "" &&
							draft.max !== "" &&
							Number(draft.min) > Number(draft.max))
					)
						return toast.error("Enter valid minimum and maximum limits.")
					editor.updateBlock(current, {
						props: {
							choices: draft.choices,
							allowOther: draft.allowOther,
							min: draft.min,
							max: draft.max,
							includesDate: draft.includesDate,
							includesTime: draft.includesTime
						}
					})
				} else if (section === "description") {
					editor.updateBlock(current, {
						props: { description: draft.description }
					})
				} else {
					editor.updateBlock(current, {
						props: {
							defaultAnswer: draft.defaultAnswer,
							defaultAnswerEnabled: draft.defaultAnswerEnabled
						}
					})
				}
				onClose()
			}}
		>
			{section === "description" ? (
				<Textarea
					aria-label="Question description"
					value={draft.description}
					onChange={(event) =>
						setDraft({ ...draft, description: event.target.value })
					}
				/>
			) : section === "defaultAnswer" ? (
				<>
					<Label className="flex items-center justify-between">
						Use a default answer
						<Switch
							checked={draft.defaultAnswerEnabled}
							onCheckedChange={(value) =>
								setDraft({ ...draft, defaultAnswerEnabled: value })
							}
						/>
					</Label>
					{draft.defaultAnswerEnabled &&
						(draft.qtype === "checkboxGroup" ? (
							<QuestionInput
								qtype="checkboxGroup"
								label="Default selections"
								options={questionOptionsFromProps(draft)}
								value={draft.defaultAnswer.split("\n").filter(Boolean)}
								onChange={(value) =>
									setDraft({
										...draft,
										defaultAnswer: Array.isArray(value) ? value.join("\n") : ""
									})
								}
								upload={async () => ({ url: "about:blank" })}
							/>
						) : choices ? (
							<Select
								value={draft.defaultAnswer}
								onValueChange={(value) =>
									setDraft({ ...draft, defaultAnswer: value })
								}
							>
								<SelectTrigger aria-label="Default answer">
									<SelectValue placeholder="Choose an option" />
								</SelectTrigger>
								<SelectContent>
									{draft.choices
										.split("\n")
										.filter(Boolean)
										.map((choice) => (
											<SelectItem key={choice} value={choice}>
												{choice}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						) : draft.qtype === "checkbox" ? (
							<Label className="flex items-center justify-between">
								Checked
								<Switch
									checked={draft.defaultAnswer === "true"}
									onCheckedChange={(value) =>
										setDraft({ ...draft, defaultAnswer: String(value) })
									}
								/>
							</Label>
						) : (
							<Input
								aria-label="Default answer"
								type={
									draft.qtype === "number"
										? "number"
										: draft.qtype === "date"
											? !draft.includesDate
												? "time"
												: draft.includesTime
													? "datetime-local"
													: "date"
											: "text"
								}
								value={draft.defaultAnswer}
								onChange={(event) =>
									setDraft({ ...draft, defaultAnswer: event.target.value })
								}
							/>
						))}
				</>
			) : choices ? (
				<>
					<Textarea
						aria-label="Choices, one per line"
						value={draft.choices}
						onChange={(event) =>
							setDraft({
								...draft,
								choices: event.target.value.replace(/\r/g, "")
							})
						}
					/>
					{draft.qtype !== "checkboxGroup" && (
						<label className="flex items-center justify-between gap-3 text-sm">
							<span>Allow an “Other” answer</span>
							<Switch
								checked={draft.allowOther}
								onCheckedChange={(allowOther) =>
									setDraft({ ...draft, allowOther })
								}
							/>
						</label>
					)}
					{draft.qtype === "checkboxGroup" && (
						<div className="grid grid-cols-2 gap-3">
							<Label className="flex flex-col gap-2">
								Minimum selections
								<Input
									type="number"
									min={0}
									step={1}
									value={draft.min}
									onChange={(event) =>
										setDraft({ ...draft, min: event.target.value })
									}
								/>
							</Label>
							<Label className="flex flex-col gap-2">
								Maximum selections
								<Input
									type="number"
									min={0}
									step={1}
									value={draft.max}
									onChange={(event) =>
										setDraft({ ...draft, max: event.target.value })
									}
								/>
							</Label>
						</div>
					)}
				</>
			) : draft.qtype === "date" ? (
				<Select
					value={
						!draft.includesDate
							? "time"
							: draft.includesTime
								? "datetime"
								: "date"
					}
					onValueChange={(value) =>
						setDraft({
							...draft,
							includesDate: value !== "time",
							includesTime: value !== "date"
						})
					}
				>
					<SelectTrigger aria-label="Date and time format">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="date">Date</SelectItem>
						<SelectItem value="time">Time</SelectItem>
						<SelectItem value="datetime">Date and time</SelectItem>
					</SelectContent>
				</Select>
			) : (
				<div className="grid grid-cols-2 gap-3">
					<Label className="flex flex-col gap-2">
						{draft.qtype === "number" ? "Minimum" : "Minimum characters"}
						<Input
							type="number"
							min={draft.qtype === "number" ? undefined : 0}
							value={draft.min}
							onChange={(event) =>
								setDraft({ ...draft, min: event.target.value })
							}
						/>
					</Label>
					<Label className="flex flex-col gap-2">
						{draft.qtype === "number" ? "Maximum" : "Maximum characters"}
						<Input
							type="number"
							min={draft.qtype === "number" ? undefined : 0}
							value={draft.max}
							onChange={(event) =>
								setDraft({ ...draft, max: event.target.value })
							}
						/>
					</Label>
				</div>
			)}
			<DialogFooter>
				<Button type="button" variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button type="submit">Save</Button>
			</DialogFooter>
		</form>
	)
}

function LogicDialogBody({
	initial,
	earlier,
	onSave,
	onCancel
}: {
	initial: FormFieldLogic
	earlier: { id: string; label: string }[]
	onSave: (logic: FormFieldLogic) => void
	onCancel: () => void
}) {
	const [logic, setLogic] = useState<FormFieldLogic>(
		() =>
			initial ??
			(earlier[0]
				? {
						action: "show",
						conditions: [
							{ fieldId: earlier[0].id, operator: "equals", value: "" }
						]
					}
				: null)
	)
	return (
		<div className="flex flex-col gap-3">
			{earlier.length === 0 ? (
				<p className="text-sm text-text-sub">
					Add another question above before setting conditions.
				</p>
			) : (
				<>
					<div className="flex items-center gap-2">
						<Select
							value={logic?.action ?? "show"}
							onValueChange={(next) =>
								setLogic({
									action: next as "show" | "hide",
									conditions: logic?.conditions ?? [
										{ fieldId: earlier[0]!.id, operator: "equals", value: "" }
									]
								})
							}
						>
							<SelectTrigger className="w-36" aria-label="Logic action">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="show">Show when</SelectItem>
								<SelectItem value="hide">Hide when</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{(logic?.conditions ?? []).map((condition, index) => (
						<div key={index} className="flex flex-wrap items-center gap-2">
							<Select
								value={condition.fieldId}
								onValueChange={(next) => {
									if (!logic) return
									const conditions = [...logic.conditions]
									conditions[index] = { ...condition, fieldId: next }
									setLogic({ ...logic, conditions })
								}}
							>
								<SelectTrigger className="w-48" aria-label="Condition question">
									<SelectValue placeholder="Question" />
								</SelectTrigger>
								<SelectContent>
									{earlier.map((question) => (
										<SelectItem key={question.id} value={question.id}>
											{question.label || "Untitled question"}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Select
								value={condition.operator}
								onValueChange={(next) => {
									if (!logic) return
									const conditions = [...logic.conditions]
									conditions[index] = {
										...condition,
										operator: next as LogicCondition["operator"]
									}
									setLogic({ ...logic, conditions })
								}}
							>
								<SelectTrigger className="w-44" aria-label="Condition operator">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{operatorLabels.map((operator) => (
										<SelectItem key={operator.value} value={operator.value}>
											{operator.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{condition.operator !== "isEmpty" &&
								condition.operator !== "isNotEmpty" && (
									<Input
										value={condition.value ?? ""}
										onChange={(event) => {
											if (!logic) return
											const conditions = [...logic.conditions]
											conditions[index] = {
												...condition,
												value: event.target.value
											}
											setLogic({ ...logic, conditions })
										}}
										aria-label="Condition value"
										placeholder="Value"
										className="w-40"
									/>
								)}
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									if (!logic) return
									const conditions = logic.conditions.filter(
										(_item, conditionIndex) => conditionIndex !== index
									)
									setLogic(conditions.length ? { ...logic, conditions } : null)
								}}
							>
								Remove condition
							</Button>
						</div>
					))}
					{earlier[0] && (
						<Button
							variant="outline"
							className="self-start"
							onClick={() =>
								setLogic({
									action: logic?.action ?? "show",
									conditions: [
										...(logic?.conditions ?? []),
										{
											fieldId: earlier[0]!.id,
											operator: "equals",
											value: ""
										}
									]
								})
							}
						>
							Add condition
						</Button>
					)}
				</>
			)}
			<DialogFooter>
				<Button variant="outline" onClick={onCancel}>
					Cancel
				</Button>
				<Button onClick={() => onSave(logic)}>Save</Button>
			</DialogFooter>
		</div>
	)
}
